// A round's money, exactly. Prices arrive as decimal strings and become fixed point (8 decimals);
// dollars are cents; everything is BigInt. The bust and the win fee follow papertrade: the bust is
// the entry moved by 1/leverage against the position, then the market's bust buffer back toward
// the entry (papertrade-rust `math::bust_price`); a win pays 2% to the venue, a loss is the loss,
// never more than the margin. papertrade's deadband and impact curve on wins are left out.

export const PRICE_DECIMALS = 8;
const SCALE = 10n ** BigInt(PRICE_DECIMALS);
const WAD = 10n ** 18n;

/** papertrade's bust buffers today (`/state/trading`), WAD: about 4.8 basis points. */
const BUST_BUFFER: Record<string, bigint> = {
  BTC: 476_041_580_087_574n,
  ETH: 481_304_050_180_130n,
};
const DEFAULT_BUFFER = BUST_BUFFER.BTC as bigint;

export const bufferOf = (symbol: string) => BUST_BUFFER[symbol] ?? DEFAULT_BUFFER;

/** `"92552.49"` → 9255249000000n. */
export function toFixed(price: string): bigint {
  const text = price.trim();
  if (!/^\d+(\.\d+)?$/.test(text)) throw new Error(`not a price: ${price}`);
  const [whole = "0", fraction = ""] = text.split(".");
  const digits = (fraction + "0".repeat(PRICE_DECIMALS)).slice(0, PRICE_DECIMALS);
  return BigInt(whole) * SCALE + BigInt(digits);
}

/** For drawing only. */
export const toNumber = (fixed: bigint) => Number(fixed) / Number(SCALE);

export function bustPrice(entry: bigint, leverage: number, long: boolean, buffer: bigint): bigint {
  const step = entry / BigInt(leverage);
  if (long) {
    const zero = entry - step;
    return zero + (zero * buffer) / WAD;
  }
  const zero = entry + step;
  return zero - (zero * buffer) / WAD;
}

/** At or past the bust: papertrade's keeper liquidates there. */
export const isBust = (price: bigint, bust: bigint, long: boolean) => (long ? price <= bust : price >= bust);

/** What closing at `exit` pays beyond the margin, in cents. */
export function pnlCents(marginCents: bigint, leverage: number, long: boolean, entry: bigint, exit: bigint): bigint {
  const move = long ? exit - entry : entry - exit;
  const raw = (marginCents * BigInt(leverage) * move) / entry;
  if (raw <= -marginCents) return -marginCents;
  if (raw > 0n) return raw - (raw * 2n) / 100n;
  return raw;
}

/** `+$4,120`, `−$1,000`, `$10,000`. Whole dollars, toward zero. */
export function dollars(cents: bigint, signed = false): string {
  const negative = cents < 0n;
  const whole = (negative ? -cents : cents) / 100n;
  const grouped = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const sign = negative ? "−" : signed && cents > 0n ? "+" : "";
  return `${sign}$${grouped}`;
}

/** A fixed-point return from `from` to `to` as a signed percentage with two decimals. */
export function percent(from: bigint, to: bigint): string {
  const bps = ((to - from) * 1_000_000n) / from; // hundredths of a basis point
  const negative = bps < 0n;
  const abs = negative ? -bps : bps;
  const whole = abs / 10_000n;
  const fraction = ((abs % 10_000n) / 100n).toString().padStart(2, "0");
  return `${negative ? "−" : "+"}${whole}.${fraction}%`;
}

/** A price as the market quotes it, from fixed point: `81,603.5`. */
export function priceText(fixed: bigint, places = 2): string {
  const negative = fixed < 0n;
  const abs = negative ? -fixed : fixed;
  const unit = 10n ** BigInt(PRICE_DECIMALS - places);
  const rounded = (abs + unit / 2n) / unit;
  const text = rounded.toString().padStart(places + 1, "0");
  const whole = text.slice(0, text.length - places).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const fraction = places ? `.${text.slice(text.length - places)}` : "";
  return `${negative ? "−" : ""}${whole}${fraction}`;
}
